import React, { useEffect, useState } from 'react';
import ReactQuill from 'react-quill';
import { toast } from 'react-toastify';

const QUILL_MODULES = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    [{ script: 'sub' }, { script: 'super' }],
    ['formula', 'link', 'image'],
    ['clean'],
  ],
};
const QUILL_FORMATS = [
  'header', 'bold', 'italic', 'underline', 'strike',
  'list', 'bullet', 'script', 'formula', 'link', 'image',
];

/**
 * Moderator-side modal to compose a comment + an optional proposed replacement
 * question HTML. Setter can later "Apply" the suggestion to the slot.
 *
 * Props:
 *   isOpen
 *   onClose
 *   slotInfo        — { path, question } context (for header)
 *   initialHtml     — prefill for the suggestion editor (e.g. from Bank picker)
 *   onOpenBank      — optional; if provided, shows "Pick from Bank" button
 *   onSubmit({ commentText, suggestedQuestionHtml }) — async
 */
function SuggestReplacementModal({
  isOpen, onClose, slotInfo, initialHtml = '', onOpenBank, onSubmit,
}) {
  const [commentText, setCommentText] = useState('');
  const [suggestion, setSuggestion] = useState('');
  const [busy, setBusy] = useState(false);

  // Reset form when the modal transitions open. We intentionally do NOT depend on
  // initialHtml here — if we did, the moderator's typed comment would be wiped
  // whenever the parent updates the prefill (e.g. after Pick-from-Bank).
  useEffect(() => {
    if (isOpen) {
      setCommentText('');
      setSuggestion('');
      setBusy(false);
    }
  }, [isOpen]);

  // Sync the Quill editor with the latest prefill. Runs both on open and on
  // parent-driven prefill updates (Pick-from-Bank flow).
  useEffect(() => {
    if (isOpen && initialHtml) setSuggestion(initialHtml);
  }, [initialHtml, isOpen]);

  if (!isOpen) return null;

  const handleSubmit = async () => {
    if (!commentText.trim()) {
      toast.error('Please write a comment explaining your suggestion');
      return;
    }
    setBusy(true);
    try {
      await onSubmit({
        commentText: commentText.trim(),
        suggestedQuestionHtml: suggestion && suggestion.trim() ? suggestion : null,
      });
    } finally {
      setBusy(false);
    }
  };

  const label = slotInfo?.question
    ? `Q${slotInfo.question.number || ''} ${slotInfo.question.marks ? `(${slotInfo.question.marks} marks)` : ''}`.trim()
    : 'Question';

  return (
    <div className="qpg-modal-backdrop" onClick={onClose}>
      <div
        className="qpg-modal"
        style={{ maxWidth: 760, width: '95%' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="qpg-modal-header">
          <div>
            <h3>Moderator Feedback — {label}</h3>
            <div className="qpg-modal-sub">
              Add a comment for the Setter. Optionally attach a proposed replacement question.
            </div>
          </div>
          <button className="qpg-modal-close" onClick={onClose}>×</button>
        </div>

        <div className="qpg-modal-body" style={{ maxHeight: '72vh', overflowY: 'auto' }}>
          <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#616161', marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.5 }}>
            Comment (required)
          </label>
          <textarea
            className="form-control"
            rows={3}
            placeholder="Explain the issue and your suggested change..."
            value={commentText}
            onChange={(e) => setCommentText(e.target.value)}
            style={{ marginBottom: 16 }}
          />

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
            <label style={{ fontSize: 12, fontWeight: 600, color: '#616161', textTransform: 'uppercase', letterSpacing: 0.5 }}>
              Proposed Replacement (optional)
            </label>
            {onOpenBank && (
              <button type="button" className="btn btn-outline btn-sm" onClick={onOpenBank}>
                Pick from Bank
              </button>
            )}
          </div>
          <ReactQuill
            theme="snow"
            value={suggestion}
            onChange={setSuggestion}
            modules={QUILL_MODULES}
            formats={QUILL_FORMATS}
            placeholder="Type a replacement question here, or pick one from the bank..."
          />
          <p className="qpg-hint" style={{ marginTop: 8 }}>
            If provided, the Setter will see an <strong>Apply Suggestion</strong> button alongside your comment.
          </p>
        </div>

        <div className="qpg-modal-footer">
          <button className="btn btn-outline" onClick={onClose} disabled={busy}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSubmit} disabled={busy || !commentText.trim()}>
            {busy ? 'Posting...' : 'Post Comment'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default SuggestReplacementModal;
